/**
 * MFA Enrollment Component (TOTP)
 *
 * Allows users to set up multi-factor authentication using authenticator apps
 * like Google Authenticator, Authy, Microsoft Authenticator, etc.
 *
 * Flow:
 * 1. User clicks "Enable MFA"
 * 2. Backend generates TOTP secret and QR code
 * 3. User scans QR code with authenticator app
 * 4. User enters 6-digit code to verify
 * 5. MFA is enabled
 */

import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, Loader2, CheckCircle, X, Copy, Check, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService from '../../services/analytics';
import { announceMfaResolved } from '../../services/mfaChallenge';
import { getWixAuthHeaders, getWixInstanceFromUrl } from '../../utils/wixInstance';

interface MFAEnrollmentProps {
  onComplete: () => void;
  onSkip?: () => void;
}

interface EnrollmentData {
  qr_code_base64: string;
  secret: string;
  manual_entry_key: string;
  issuer: string;
}

export default function MFAEnrollment({ onComplete, onSkip }: MFAEnrollmentProps) {
  const { getAuthToken, loading: authLoading } = useAuth();

  const [step, setStep] = useState<'loading' | 'scan' | 'verify' | 'backup' | 'success'>('loading');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [backupCodesError, setBackupCodesError] = useState<string | null>(null);
  const [savingCodes, setSavingCodes] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData | null>(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const API_URL = import.meta.env.VITE_API_URL || '';

  /**
   * Choose the credential this enrolment authenticates with.
   *
   * Firebase wins when present: it is the identity that persists across every
   * surface, so a user who is signed in both ways enrols the account they
   * keep rather than the dashboard they happen to be looking at.
   *
   * Falling back to the Wix instance token is what makes this page work at
   * all for a Wix merchant - they have no Firebase user, and the backend
   * accepts `Authorization: WixInstance <token>` on these routes.
   *
   * With neither credential we throw instead of sending `Bearer null`, which
   * is what the previous version did: the request failed with an opaque 401
   * and the user was left on a dead form with no idea why.
   */
  const buildAuthHeaders = async (): Promise<Record<string, string>> => {
    const firebaseToken = await getAuthToken();
    if (firebaseToken) {
      return {
        'Authorization': `Bearer ${firebaseToken}`,
        'Content-Type': 'application/json',
      };
    }

    const instanceToken = getWixInstanceFromUrl();
    if (instanceToken) {
      return getWixAuthHeaders(instanceToken);
    }

    throw new Error(
      'Could not verify your identity for MFA setup. Please reopen the app ' +
        'from your Wix dashboard, or sign in again.'
    );
  };

  // Start enrollment once Firebase has finished restoring the session.
  //
  // ProtectedRoute used to hold this page behind its `loading` check.
  // /setup-mfa is now guarded by MfaChallengeGuard, which deliberately has no
  // auth requirement - a Wix merchant has no Firebase user at all - and so
  // does not wait. Without this guard a direct load, reload, bookmark or
  // restored tab starts enrolment while `auth.currentUser` is still null, and
  // a perfectly well signed-in web user is told to "reopen the app from your
  // Wix dashboard". That is the recovery page for a locked-out account, so it
  // has to be right on first paint, not after a retry.
  //
  // Wix merchants are unaffected either way: their credential is the
  // `instance` query parameter, and `loading` resolves to false for them as
  // soon as Firebase reports no user.
  useEffect(() => {
    if (authLoading) return;
    analyticsService.trackMfaEnrollmentStart();
    startEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  // Focus first code input when moving to verify step
  useEffect(() => {
    if (step === 'verify') {
      inputRefs.current[0]?.focus();
    }
  }, [step]);

  const startEnrollment = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/v1/mfa/enroll`, {
        method: 'POST',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        // 400 "MFA is already enabled" is not a failure the user can act on
        // by retrying - it means they are already protected. Reaching this
        // screen in that state means a stale enrollment challenge sent them
        // here, so revoke it and let them continue instead of stranding them
        // on a form whose submit button can never work (handleVerify
        // early-returns while enrollmentData is null).
        const detail: string = data.detail || '';
        if (response.status === 400 && /already enabled/i.test(detail)) {
          announceMfaResolved();
          setStep('success');
          setTimeout(onComplete, 1200);
          return;
        }

        throw new Error(detail || 'Failed to start MFA enrollment');
      }

      const data: EnrollmentData = await response.json();
      setEnrollmentData(data);
      setStep('scan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start enrollment');
      setStep('scan'); // Show UI even on error so user can retry
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newCode = [...code];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setCode(newCode);
        inputRefs.current[Math.min(digits.length, 5)]?.focus();
      });
    }
  };

  const generateBackupCodes = async () => {
    setSavingCodes(true);
    setBackupCodesError(null);

    try {
      const response = await fetch(`${API_URL}/api/v1/mfa/backup-codes/generate`, {
        method: 'POST',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not generate backup codes');
      }

      const data: { codes: string[] } = await response.json();
      setBackupCodes(data.codes);
    } catch (err) {
      // Deliberately NOT fatal. MFA is already enabled at this point, so
      // blocking here would strand a protected user on a screen they cannot
      // complete - the same trap this flow exists to avoid. Surface the
      // failure, offer a retry, and let them continue to Settings.
      setBackupCodesError(
        err instanceof Error ? err.message : 'Could not generate backup codes',
      );
    } finally {
      setSavingCodes(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!backupCodes) return;
    const body = [
      'ContentSage - two-factor recovery codes',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'Each code works once. Store them somewhere you can reach',
      'WITHOUT your authenticator app.',
      '',
      ...backupCodes,
    ].join('\n');

    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `contentsage-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6 || !enrollmentData) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/v1/mfa/verify`, {
        method: 'POST',
        headers: await buildAuthHeaders(),
        body: JSON.stringify({
          code: fullCode,
          secret: enrollmentData.secret,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.verified) {
        analyticsService.trackMfaVerification(false);
        throw new Error(data.message || data.detail || 'Invalid code');
      }

      analyticsService.trackMfaVerification(true);
      analyticsService.trackMfaEnrollmentComplete();

      // Revoke the challenge BEFORE navigating. The route guard reads it from
      // module scope, so without this the redirect to /dashboard below is
      // immediately undone and the user is bounced straight back here.
      announceMfaResolved();

      // Enrolment without recovery codes is a lockout waiting to happen:
      // /mfa/disable requires a TOTP or backup code, and there is no admin
      // reset, so a lost authenticator means direct database surgery. The
      // generate endpoint has always existed - nothing on this path called
      // it, and only the Settings page did, which a user driven here by
      // enforcement has no reason to visit.
      setStep('backup');
      await generateBackupCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when code is complete
  useEffect(() => {
    if (code.every((d) => d !== '') && step === 'verify') {
      handleVerify();
    }
  }, [code, step]);

  const copyManualKey = () => {
    if (enrollmentData?.manual_entry_key) {
      navigator.clipboard.writeText(enrollmentData.manual_entry_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (step === 'backup') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 max-w-md mx-auto"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
          Two-factor authentication is on
        </h3>
        <p className="text-gray-600 text-center mb-6">
          Save these recovery codes before you continue.
        </p>

        {savingCodes && (
          <div className="flex items-center justify-center gap-2 py-8 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating your recovery codes...
          </div>
        )}

        {backupCodes && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                These are shown <strong>once</strong>. Each works a single time, and
                they are the only way back in if you lose your authenticator.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((backupCode) => (
                <code
                  key={backupCode}
                  className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm font-mono text-gray-900 text-center"
                >
                  {backupCode}
                </code>
              ))}
            </div>

            <button
              onClick={downloadBackupCodes}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 mb-3"
            >
              <Download className="w-4 h-4" />
              Download codes
            </button>
          </>
        )}

        {backupCodesError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Could not generate recovery codes</p>
                <p className="mt-1">{backupCodesError}</p>
                <p className="mt-1">
                  Two-factor authentication is still enabled. Generate codes from
                  Settings as soon as you can.
                </p>
              </div>
            </div>
            <button
              onClick={generateBackupCodes}
              className="mt-3 text-sm font-medium text-red-700 underline"
            >
              Try again
            </button>
          </div>
        )}

        {!savingCodes && (
          <button
            onClick={() => {
              setStep('success');
              setTimeout(onComplete, 1200);
            }}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700"
          >
            {backupCodes ? "I've saved my codes - continue" : 'Continue'}
          </button>
        )}
      </motion.div>
    );
  }

  if (step === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center max-w-md mx-auto"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">MFA Enabled!</h3>
        <p className="text-gray-600">
          Your account is now protected with two-factor authentication.
        </p>
      </motion.div>
    );
  }

  if (step === 'loading') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center max-w-md mx-auto"
      >
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Setting up MFA...</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 max-w-md mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Enable Two-Factor Auth</h3>
            <p className="text-sm text-gray-500">Use an authenticator app</p>
          </div>
        </div>
        {onSkip && (
          <button
            onClick={() => {
              analyticsService.trackMfaEnrollmentSkip();
              onSkip();
            }}
            className="p-2 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </motion.div>
      )}

      {step === 'scan' && enrollmentData && (
        <>
          {/* QR Code */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4 text-center">
              Scan this QR code with your authenticator app
              (Google Authenticator, Authy, etc.)
            </p>
            <div className="flex justify-center">
              <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
                <img
                  src={`data:image/png;base64,${enrollmentData.qr_code_base64}`}
                  alt="MFA QR Code"
                  className="w-48 h-48"
                />
              </div>
            </div>
          </div>

          {/* Manual Entry Key */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-500 mb-2">Can't scan? Enter this key manually:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono bg-white px-3 py-2 rounded border border-gray-200 break-all">
                {enrollmentData.manual_entry_key}
              </code>
              <button
                onClick={copyManualKey}
                className="p-2 text-gray-500 hover:text-gray-700 transition"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={() => setStep('verify')}
            className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition"
          >
            I've Scanned the QR Code
          </button>
        </>
      )}

      {step === 'scan' && !enrollmentData && (
        <div className="text-center">
          <p className="text-gray-600 mb-4">Failed to load QR code.</p>
          <button
            onClick={startEnrollment}
            className="bg-purple-600 text-white px-6 py-2 rounded-xl font-semibold hover:bg-purple-700 transition"
          >
            Try Again
          </button>
        </div>
      )}

      {step === 'verify' && (
        <>
          {/* Verification Code Input */}
          <p className="text-sm text-gray-600 mb-6 text-center">
            Enter the 6-digit code from your authenticator app
          </p>

          <div className="flex justify-center gap-2 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={loading}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition disabled:opacity-50"
              />
            ))}
          </div>

          <button
            onClick={handleVerify}
            disabled={loading || code.some((d) => !d)}
            className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Enable MFA'
            )}
          </button>

          <button
            onClick={() => {
              setStep('scan');
              setCode(['', '', '', '', '', '']);
              setError(null);
            }}
            className="w-full mt-3 text-gray-600 font-medium hover:text-gray-800 transition"
          >
            Back to QR Code
          </button>
        </>
      )}

      {/* Skip Option */}
      {onSkip && step === 'scan' && (
        <button
          onClick={() => {
            analyticsService.trackMfaEnrollmentSkip();
            onSkip();
          }}
          className="w-full mt-4 text-gray-500 text-sm hover:text-gray-700 transition"
        >
          I'll do this later
        </button>
      )}
    </motion.div>
  );
}
